import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';
import {
  AmlCase,
  AmlCasesResponse,
  AmlFilter,
  AmlStats,
  DecisionFormState,
} from './types';
import AmlCaseList from './AmlCaseList';
import AmlCaseDetail from './AmlCaseDetail';
import { useMenuBadge } from '../../providers/MenuBadgeProvider';

const AML_REFRESH_INTERVAL = 30000; // 30 секунд

// Начальные фильтры
// status='active' — pending+in_review (см. v48 UX-1). Соответствует
// MenuBadgeProvider count: оператор кликает по бейджу "5" → видит ровно
// 5 кейсов. Backend repo расширен на 'active' → WHERE status IN ('pending','in_review').
const defaultFilter: AmlFilter = {
  status:    'active',
  riskLevel: '',
  search:    '',
  page:      1,
  size:      25,
};

// Главный компонент вкладки AML
const AmlTab: React.FC = () => {
  const [cases, setCases]           = useState<AmlCase[]>([]);
  const [total, setTotal]           = useState(0);
  const [stats, setStats]           = useState<AmlStats | null>(null);
  const [filter, setFilter]         = useState<AmlFilter>(defaultFilter);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<AmlCase | null>(null);
  const [autoRefresh, setAutoRefresh]   = useState(false);

  // refresh из MenuBadgeProvider — после любого изменения списка (фильтры, decision)
  // дёргаем общий счётчик, чтобы sidebar-badge и колокольчик отражали свежие данные.
  const { refresh: refreshMenuBadge } = useMenuBadge();

  const intervalRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorCountRef = useRef(0);

  // Загрузка кейсов
  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await (ApiService as any).amlGetCases(filter) as AmlCasesResponse;
      setCases(resp.data);
      setTotal(resp.total);
      setError(null);
      errorCountRef.current = 0;
    } catch (e: any) {
      errorCountRef.current++;
      setError('Ошибка загрузки AML-кейсов');
      if (errorCountRef.current >= 3) setAutoRefresh(false);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Загрузка статистики
  const fetchStats = useCallback(async () => {
    try {
      const s = await (ApiService as any).amlGetStats() as AmlStats;
      setStats(s);
    } catch {
      // Статистика не критична — молча пропускаем
    }
  }, []);

  // Первичная загрузка и при изменении фильтров
  useEffect(() => {
    fetchCases();
    fetchStats();
  }, [fetchCases, fetchStats]);

  // При открытии вкладки + после принятия решения — синхронизируем общий
  // счётчик MenuBadge, чтобы sidebar/колокольчик не отставали. Без этого
  // оператор только что обработал кейс, но в sidebar всё ещё видит старое число.
  useEffect(() => {
    refreshMenuBadge();
  }, [refreshMenuBadge, cases.length]);

  // Автообновление
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchCases();
        fetchStats();
      }, AML_REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchCases, fetchStats]);

  // Обновление фильтров (с частичным мержем)
  const handleFilterChange = useCallback((partial: Partial<AmlFilter>) => {
    setFilter((prev) => ({ ...prev, ...partial }));
    // При смене фильтра (не пагинации) сбрасываем страницу
    if (!('page' in partial)) {
      setFilter((prev) => ({ ...prev, ...partial, page: 1 }));
    }
  }, []);

  // Открытие детальной карточки — загружаем полные данные (с правилами и
  // историей).
  const handleSelectCase = useCallback(async (c: AmlCase) => {
    setSelectedCase(c);
    try {
      const full = await (ApiService as any).amlGetCase(c.id) as AmlCase;
      setSelectedCase(full);
    } catch {
      // оставляем данные из списка если запрос не удался
    }
  }, []);

  // v48 SH-1: deep-link из /scoring-history → /finmonitoring?caseId=AML-...&alertId=<uuid>
  // При первом рендере (и при изменении query-params) подгружаем выбранный кейс.
  // Используем alertId (UUID) если есть — он стабильнее чем caseId (текст).
  // Кейс может ещё отсутствовать в `cases`-state (пагинация), поэтому грузим напрямую.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const alertId = searchParams.get('alertId');
    const caseId  = searchParams.get('caseId');
    if (!alertId && !caseId) return;
    if (selectedCase && (selectedCase.id === alertId || selectedCase.caseId === caseId)) return;

    (async () => {
      try {
        // Если есть UUID alertId — грузим напрямую.
        if (alertId) {
          const full = await (ApiService as any).amlGetCase(alertId) as AmlCase;
          setSelectedCase(full);
          return;
        }
        // Иначе ищем по caseId (например AML-2026-0025) в текущей выдаче,
        // расширяя фильтр на 'active' если не нашли.
        const found = cases.find((c) => c.caseId === caseId);
        if (found) {
          handleSelectCase(found);
        }
      } catch {
        // тихо игнорируем — оператор сам выберет
      } finally {
        // чистим query, чтобы повторный рендер не дёргал заново
        const next = new URLSearchParams(searchParams);
        next.delete('caseId');
        next.delete('alertId');
        setSearchParams(next, { replace: true });
      }
    })();
    // Намеренно зависим только от searchParams — иначе useEffect перезапустится
    // при каждом изменении `cases`/`selectedCase` и снова подгрузит кейс.
    // eslint-disable-next-line
  }, [searchParams]);

  // Принятие решения по кейсу
  const handleDecisionSubmit = async (caseId: string, form: DecisionFormState) => {
    await (ApiService as any).amlMakeDecision(caseId, form);
    await fetchCases();
    await fetchStats();
    setSelectedCase(null);
  };

  return (
    <div>
      {/* Заголовок со статистикой */}
      {stats && (
        <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <StatBadge label="Всего"         value={stats.total}       variant="secondary" />
          <StatBadge label="Ожидают"       value={stats.pending}     variant="warning"   />
          <StatBadge label="На проверке"   value={stats.inReview}    variant="info"      />
          <StatBadge label="Просрочено"    value={stats.overdue}     variant="danger"    />
          <StatBadge label="Критических"   value={stats.criticalCount} variant="danger"  />
          <StatBadge label="Эскалировано"  value={stats.escalated}   variant="dark"      />

          <span className="text-muted small ms-auto">
            Среднее время: <strong>{stats.avgResolutionHours} ч</strong>
          </span>
        </div>
      )}

      {/* Панель управления */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="aml-auto-refresh"
            label={`Автообновление (${AML_REFRESH_INTERVAL / 1000} сек)`}
            checked={autoRefresh}
            onChange={() => setAutoRefresh((v) => !v)}
          />
        </div>
        <Button variant="outline-primary" size="sm" onClick={() => { fetchCases(); fetchStats(); }}>
          Обновить
        </Button>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Основной layout: таблица (слева) + детальная панель (справа, если выбрана) */}
      <div className={`d-flex gap-3 ${selectedCase ? 'align-items-start' : ''}`}>
        {/* Таблица кейсов */}
        <div style={{ flex: selectedCase ? '0 0 55%' : '1', minWidth: 0 }}>
          {loading && !cases.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="text-muted mt-2 small">Загрузка AML-кейсов...</div>
            </div>
          ) : (
            <AmlCaseList
              cases={cases}
              selectedId={selectedCase?.id ?? null}
              onSelect={handleSelectCase}
              filter={filter}
              onFilterChange={handleFilterChange}
              total={total}
            />
          )}
        </div>

        {/* Детальная карточка */}
        {selectedCase && (
          <div style={{ flex: '0 0 44%', minWidth: 0 }}>
            <Card className="border">
              <Card.Body>
                <AmlCaseDetail
                  amlCase={selectedCase}
                  onDecisionSubmit={handleDecisionSubmit}
                  onClose={() => setSelectedCase(null)}
                />
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Вспомогательный компонент — карточка-счётчик в заголовке
interface StatBadgeProps {
  label: string;
  value: number;
  variant: string;
}
const StatBadge: React.FC<StatBadgeProps> = ({ label, value, variant }) => (
  <div className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: '0.82rem' }}>
    <span className="text-muted">{label}:</span>
    <Badge bg={variant}>{value}</Badge>
  </div>
);

export default AmlTab;
